/**
 * adjustBidLocalFilters.js
 * -------------------------
 * Helpers for AdjustBidV2 (Placement Bids v2).
 *
 * Bid / strategy come from the wrapper API
 * (`bid_adjustment_value`, `campaign_bid_strategy`).
 * Filters for those fields are sent as normal wrapper filters (no local
 * campaign-id injection). Bid Strategy filter rules live under
 * `filters.dimensions`; Bid under `filters.measures`.
 */

import {
  clearAllSelections,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSB,
  isCampaignTypeSP,
  prepareWrapperFilters,
  SB_SD_OPTIMISATION_MODAL_NOTE,
  SB_SD_OPTIMISATION_TOOLTIP,
} from "../../helper/insights/insightsFilterUtils";

export {
  clearAllSelections,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSB,
  isCampaignTypeSP,
  prepareWrapperFilters,
  SB_SD_OPTIMISATION_MODAL_NOTE,
  SB_SD_OPTIMISATION_TOOLTIP,
};

/** Always requested in `query.measures` (in addition to page measures + group-by dims). */
export const PLACEMENT_BIDS_ALWAYS_MEASURES = [
  "bid_adjustment_value",
  "campaign_bid_strategy",
  "campaign_format",
  "campaign_type",
];

/** Hidden from Placement Bids V2 table + ComplexFilter (shared Performance configs). */
export const PLACEMENT_BIDS_V2_HIDDEN_COLUMN_KEYS = ["campaign_burn_rate"];
export const PLACEMENT_BIDS_V2_HIDDEN_MEASURE_FILTER_IDS = ["campaign_burn_rate"];

/** Columns that are not sortable via API. */
export const NON_SORTABLE_COLUMN_KEYS = [];

export const DEFAULT_PLACEMENT_DIMENSIONS = [
  "campaign_id",
  "campaign_name",
  "placement_name",
];

/** @deprecated Use isCampaignTypeSB — kept as alias during rename. */
export function isCampaignFormatSB(campaignTypeOrFormat) {
  return isCampaignTypeSB(campaignTypeOrFormat);
}

/** @deprecated Use isCampaignTypeSP — kept as alias during rename. */
export function isCampaignFormatSP(campaignTypeOrFormat) {
  return isCampaignTypeSP(campaignTypeOrFormat);
}

/**
 * Normalizes a wrapper insights row for the table / edit UI.
 * Maps bid_adjustment_value → bid / bid_copy for the existing edit controls.
 */
export function normalizePlacementInsightRow(row) {
  const bidValue = row?.bid_adjustment_value;
  const campaignType = row?.campaign_type;
  return {
    ...row,
    bid: bidValue,
    bid_copy: bidValue,
    isChecked: false,
    isEditable: false,
    isDisabled: isCampaignTypeSB(campaignType),
  };
}

export function normalizePlacementInsightRows(rows) {
  return (rows || []).map((row) => normalizePlacementInsightRow(row));
}

/** Rename Bid Adjustment Value → Current Bid; drop V2-hidden columns (e.g. DBR). */
export function mapPlacementBidColumns(columns = []) {
  const attributeKeys = new Set([
    "campaign_bid_strategy",
    "campaign_format",
    "campaign_type",
  ]);

  return (columns || [])
    .filter(
      (column) => !PLACEMENT_BIDS_V2_HIDDEN_COLUMN_KEYS.includes(column?.key)
    )
    .map((column) => {
      let next = { ...column };

      if (attributeKeys.has(column?.key)) {
        next = { ...next, isAttribute: true };
        delete next.viewType;
      }

      if (column?.key === "bid_adjustment_value" || column?.key === "bid") {
        next = {
          ...next,
          title: "Current Bid",
          shortTitle: "Current Bid",
          format: "percent",
          isCurrencyFormat: false,
          viewType: ["PERFORMANCE", "FUNNEL"],
        };
      }

      if (
        !next.isDimension &&
        !next.isAttribute &&
        !next.viewType?.length
      ) {
        next = { ...next, isAttribute: true };
      }

      return next;
    });
}

/** Strip V2-hidden measure filters (e.g. DBR) without mutating shared Performance config. */
export function getPlacementBidsV2MeasureFilterConfig(baseConfig) {
  if (!baseConfig) return baseConfig;
  return {
    ...baseConfig,
    fields: (baseConfig.fields || []).filter(
      (field) =>
        !PLACEMENT_BIDS_V2_HIDDEN_MEASURE_FILTER_IDS.includes(field?.id)
    ),
  };
}

export function resolveDefaultGroupByDimensions(
  groupByOptions,
  defaultDimensions = DEFAULT_PLACEMENT_DIMENSIONS
) {
  const match = (groupByOptions || []).find(
    (option) =>
      Array.isArray(option?.dimensions) &&
      defaultDimensions.every((dimension) => option.dimensions.includes(dimension))
  );
  return match?.dimensions?.length ? match.dimensions : defaultDimensions;
}

export function buildPlacementInsightsQueryBody({
  queryDimensions = [],
  filters = {},
  campaignsFilter,
  queryMeasure = [],
  queryOrder = [["spends", "desc"]],
  dateRange = [],
  offset = 0,
  limit = 50,
}) {
  const measures = [
    ...new Set([
      ...(queryMeasure || []),
      ...PLACEMENT_BIDS_ALWAYS_MEASURES,
      ...(queryDimensions || []),
    ]),
  ];

  return {
    query: {
      dimensions: queryDimensions,
      filters: filters || {},
      extra_filters: {
        type: "amazon",
        amazon_campaigns_filter: campaignsFilter,
      },
      percentile_filters: [],
      measures,
      order: queryOrder,
      date_preset: null,
      timeDimensions: [{ dimension: "date", dateRange }],
      offset,
      limit,
    },
  };
}

export function toggleSingleSelection(rows, index) {
  return (rows || []).map((row, i) => {
    if (i !== index || !isCampaignTypeSP(row?.campaign_type)) return row;
    return { ...row, isChecked: !row.isChecked };
  });
}

export function toggleSelectAll(rows, checked) {
  return (rows || []).map((row) => {
    if (!isCampaignTypeSP(row?.campaign_type)) return row;
    return { ...row, isChecked: checked };
  });
}
